import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { ArrowUp, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { AppHeaderBar, PanelTogglePlaceholder } from "@/components/ui/app-header";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { peekSkeletonComposerText, requestSkeletonComposerSubmit, saveSkeletonComposerText } from "./newSessionSkeletonState";
import { NEW_SESSION_MASCOT_SIZE_CLASS, NEW_SESSION_SCROLL_AREA_CLASS, NEW_SESSION_WELCOME_COLUMN_CLASS } from "./newSessionLayout";
import { PROMPT_PILL_ROW_CLASS, PromptPillSkeleton, LEAD_STARTER_PILL_LAYOUT_CLASS, STARTER_PILL_ROW_CLASS, StarterPromptPillSkeleton } from "./promptPills";
import { SidebarSkeleton } from "./SidebarSkeleton";
import { readLeftSidebarSkeletonLayout, useIsHydrated } from "./skeletonLayout";
import { useIsTouchPrimary } from "@/hooks/use-device";
import { assetUrl } from "@/lib/assetUrl";
import { useCodesmithSurface } from "@/lib/codesmithSurface";
import { splitInstanceBasePath } from "@/lib/dashboard-routes";
import { cn } from "@repo/blacksmith-ui/cn";
import { CENTERED_LOADING_ANVIL_SIZE_CLASS, CENTERED_LOADING_ANVIL_SIZE_PX } from "@/components/agent-session/centeredLoadingAnvil";
import { useAnvilHandoffSource } from "@/components/agent-session/useAnvilHandoff";
import { animationPath } from "@/components/agent-session/useAnvilTransition";

/* ---------------------------------------------------------------------------
 * Structural skeletons for the Codesmith session view.
 *
 * SessionLoadingScreen    – centered animated anvil for existing sessions.
 *                           Used inside Codesmith.tsx where the real sidebar
 *                           is already rendered alongside.
 *
 * NewSessionChatSkeleton  – the new-session welcome layout (centered mascot,
 *                           greeting, composer, starter rows).
 *
 * SessionSkeleton         – sidebar + main pane. Used in the page-level
 *                           Suspense fallback where the entire Codesmith view
 *                           is replaced; picks the main-pane variant from the
 *                           current route.
 * -------------------------------------------------------------------------*/

const LOADING_LOOP = "ruminating";

/** Loading state for an existing session. */
export function SessionLoadingScreen() {
  const $ = _c(1);
  const spriteRef = useRef(null);
  useAnvilHandoffSource(spriteRef, LOADING_LOOP);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="status" aria-label="Loading session" className="flex min-w-0 flex-1 items-center justify-center bg-[var(--color-app-background)]"><span ref={spriteRef} aria-hidden={true} className={cn(CENTERED_LOADING_ANVIL_SIZE_CLASS, "flex items-center justify-center")}><img src={animationPath(LOADING_LOOP, "light")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="select-none object-contain [image-rendering:pixelated] dark:hidden" /><img src={animationPath(LOADING_LOOP, "dark")} alt="" width={CENTERED_LOADING_ANVIL_SIZE_PX} height={CENTERED_LOADING_ANVIL_SIZE_PX} draggable={false} className="hidden select-none object-contain [image-rendering:pixelated] dark:block" /></span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * `NewSessionView` mounts PrActionCards only when `pr_preview` hydrates a
 * `previewManagedPr` (see Codesmith.tsx) — the "View in Codesmith" deep link
 * carries the PR metadata as JSON in that param. A bare `pr` param (a
 * resolve-pr landing with no metadata, or a `repo`+`pr` Fix-with-Codesmith
 * link) resolves into TaskStarterCards instead, so only `pr_preview` reserves
 * the PrActionCards skeleton shape (four action pills).
 */
function hasPrPreviewInSearch(search: string): boolean {
  return new URLSearchParams(search).has("pr_preview");
}

/** Placeholder pill count matching PrActionCards' four action pills. */
const PR_ACTION_PILL_COUNT = 4;

/**
 * Mirrors NewSessionView's non-compact layout so the fallback resolves into
 * the real page without reshaping. The mascot and composer are static chrome
 * rendered for real; the user-dependent greeting and starter rows shimmer.
 * The composer is a live textarea whose text hands off
 * to the real composer's draft (see newSessionSkeletonState), so the user
 * can start typing before the app finishes loading. Enter follows the real
 * composer's keymap (send on desktop, newline on touch) and the send button
 * works like Enter; the send itself waits for the real composer.
 */
export function NewSessionChatSkeleton(t0) {
  const $ = _c(43);
  const {
    search
  } = t0;
  const isHydrated = useIsHydrated();
  const isTouch = useIsTouchPrimary();
  let t1;
  if ($[0] !== isHydrated || $[1] !== search) {
    t1 = isHydrated && hasPrPreviewInSearch(search ?? window.location.search);
    $[0] = isHydrated;
    $[1] = search;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isPrAttached = t1;
  const seededText = isHydrated ? peekSkeletonComposerText() ?? "" : "";
  const textareaRef = useRef(null);
  const [typedHasText, setTypedHasText] = useState(null);
  const hasText = typedHasText ?? seededText.trim().length > 0;
  const requestSubmit = _temp;
  let t2;
  if ($[3] !== isHydrated) {
    t2 = isHydrated && !readLeftSidebarSkeletonLayout().visible && <PanelTogglePlaceholder />;
    $[3] = isHydrated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <AppHeaderBar inset="canvas" className="bg-app-background">{t2}</AppHeaderBar>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="relative mt-1"><img src={assetUrl("anvil-hello-light.png")} alt="[code]smith" className={`${NEW_SESSION_MASCOT_SIZE_CLASS} dark:hidden`} /><img src={assetUrl("anvil-hello-dark.png")} alt="[code]smith" className={`hidden ${NEW_SESSION_MASCOT_SIZE_CLASS} dark:block`} /></div>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex flex-col items-center">{t4}<div className="flex h-[25px] items-center"><Skeleton className="h-5 w-72 max-w-full rounded" /></div></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isHydrated || $[10] !== seededText) {
    t6 = el => {
      textareaRef.current = el;
      if (!el) {
        return;
      }
      const handoff = peekSkeletonComposerText() ?? "";
      if (isHydrated && handoff !== seededText) {
        el.value = handoff;
      }
      el.setSelectionRange(el.value.length, el.value.length);
    };
    $[9] = isHydrated;
    $[10] = seededText;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== setTypedHasText) {
    t7 = e => {
      saveSkeletonComposerText(e.target.value);
      setTypedHasText(e.target.value.trim().length > 0);
    };
    $[12] = setTypedHasText;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isTouch) {
    t8 = e_0 => {
      if (e_0.nativeEvent.isComposing || e_0.keyCode === 229) {
        return;
      }
      if (e_0.key !== "Enter" || e_0.shiftKey || isTouch) {
        return;
      }
      e_0.preventDefault();
      requestSubmit(e_0.currentTarget.value);
    };
    $[14] = isTouch;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== seededText || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <textarea ref={t6} rows={1} autoFocus={true} autoComplete="off" data-1p-ignore={true} spellCheck={false} aria-label="Chat input" placeholder="Got a task or question?" defaultValue={seededText} onChange={t7} onKeyDown={t8} className="type-chat-prose pointer-events-auto w-full resize-none overflow-hidden border-0 bg-transparent p-0 pl-[31px] md:pl-[29px] pr-9 text-neutral-900 placeholder-neutral-500 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 dark:text-neutral-100 dark:placeholder-neutral-500/70" />;
    $[16] = seededText;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute inset-y-0 left-0 flex items-center gap-1"><span className="-ml-2 md:-ml-[10px] flex size-8 items-center justify-center rounded-full text-icon-secondary"><Plus className="size-[18px]" strokeWidth={2} aria-hidden={true} /></span></div>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = !hasText;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => requestSubmit(textareaRef.current?.value ?? "");
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = hasText ? "cursor-pointer border-[#2a2c2c] bg-blacksmith-yellow text-neutral-900 hover:brightness-95 dark:text-neutral-900" : "cursor-not-allowed border-black/[0.08] bg-[var(--brand-gradient-yellow-tertiary)] text-neutral-400 dark:text-neutral-500";
  let t14;
  if ($[23] !== t13) {
    t14 = cn("pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border transition-all focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:outline-none focus-visible:ring-0", t13);
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ArrowUp className="size-3.5" strokeWidth={2.5} aria-hidden={true} />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t11 || $[27] !== t14) {
    t16 = <div className="absolute inset-y-0 right-0 flex items-center"><button type="button" aria-label="Send message" disabled={t11} onClick={t12} className={t14}>{t15}</button></div>;
    $[26] = t11;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16 || $[30] !== t9) {
    t17 = <div className="rounded-[32px] border border-[var(--color-border)] bg-white pl-3 pr-1.5 py-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.08)] md:pl-4 md:pr-2 md:py-1.5 dark:bg-neutral-900 dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.3)]"><div className="relative flex min-h-8 items-center">{t9}{t10}{t16}</div></div>;
    $[29] = t16;
    $[30] = t9;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = isPrAttached ? PROMPT_PILL_ROW_CLASS : STARTER_PILL_ROW_CLASS;
  let t19;
  if ($[32] !== isPrAttached) {
    t19 = isPrAttached ? Array.from({
      length: PR_ACTION_PILL_COUNT
    }, _temp2) : <><StarterPromptPillSkeleton className={LEAD_STARTER_PILL_LAYOUT_CLASS} /><StarterPromptPillSkeleton className="grow" /></>;
    $[32] = isPrAttached;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <div className="flex w-full flex-col gap-4"><div className={t18}>{t19}</div></div>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t17 || $[38] !== t20) {
    t21 = <div className={NEW_SESSION_SCROLL_AREA_CLASS}><div className={`relative mb-auto flex max-w-full flex-col px-4 ${NEW_SESSION_WELCOME_COLUMN_CLASS}`}>{t5}<div className="flex w-full flex-col gap-3">{t17}{t20}</div></div></div>;
    $[37] = t17;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t21 || $[41] !== t3) {
    t22 = <div className="pointer-events-none flex min-w-0 flex-1 flex-col bg-[var(--color-app-background)]">{t3}{t21}</div>;
    $[40] = t21;
    $[41] = t3;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  return t22;
}

/**
 * `/[org]/sessions` and `/[org]/sessions/new` land on NewSessionView, so the
 * fallback should be welcome-shaped there rather than chat-transcript-shaped.
 * An enterprise instance prefix (`/enterprise/ghe/acme/sessions/new`) is
 * stripped first.
 */
function _temp2(_, i) {
  return <PromptPillSkeleton key={i} className="w-32" />;
}
function _temp(text) {
  if (text.trim()) {
    requestSkeletonComposerSubmit(text);
  }
}
function isNewSessionPath(path: string): boolean {
  const {
    pathname
  } = splitInstanceBasePath(path.split(/[?#]/)[0]);
  const segments = pathname.split("/").filter(Boolean);
  return segments[1] === "sessions" && (segments.length === 2 || segments[2] === "new");
}

/** Full skeleton (sidebar + main pane) for the page-level Suspense fallback. */
export function SessionSkeleton() {
  const $ = _c(13);
  const router = useRouter();
  const surface = useCodesmithSurface(router.query.embed);
  const isHydrated = useIsHydrated();
  let t0;
  if ($[0] !== isHydrated || $[1] !== router.asPath) {
    t0 = isHydrated && router.asPath.includes("/[") ? window.location.pathname + window.location.search : router.asPath;
    $[0] = isHydrated;
    $[1] = router.asPath;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const path = t0;
  let t1;
  if ($[3] !== path) {
    t1 = path.split("#")[0].match(/\?.*/)?.[0] ?? "";
    $[3] = path;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const search = t1;
  const t2 = surface !== "extension";
  let t3;
  if ($[5] !== t2) {
    t3 = <SidebarSkeleton showSearch={t2} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== path || $[8] !== search) {
    t4 = isNewSessionPath(path) ? <NewSessionChatSkeleton search={search} /> : <SessionLoadingScreen />;
    $[7] = path;
    $[8] = search;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="pointer-events-none flex h-full">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}